import { useState, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';

import Textbox from '../../components/textbox/Textbox';
import Button from '../../components/button/Button';
import API, { ErrorWithDetails } from '../../utils/api';

import { ReactComponent as DiscordLogo } from "../../svg/discord-mark-white.svg";

import './SignupPage.scss';
import { InternalLink } from '../../components/internal-link/InternalLink';

function LoginPage() {

  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const errorCode = parseInt(query.get("error") || "0") || 0;
  const initialError = errorCode === 401
    ? new Error('Your session has expired. Please log in again.')
    : null;

  const [error, setError] = useState<ErrorWithDetails | null>(initialError);
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState(query.get("email") || "");

  const emailRef = useRef(null);
  const passwordRef = useRef(null);

  const handleLogin = async () => {
    if (isLoading) {
      return;
    }
    setError(null);
    setIsLoading(true);
    const loginParams = {
      username: (emailRef.current || {value: ''}).value,
      password: (passwordRef.current || {value: ''}).value,
    };
    try {
      // Don't set isLoading to false here
      // Keep the loading until nav away
      await API.login(loginParams);
      navigate('/chat');
    } catch (e) {
      setError(e as ErrorWithDetails);
      setIsLoading(false);
    }
  };

  const handleDiscordLogin = async () => {
    if (isLoading) {
      return;
    }
    setError(null);
    setIsLoading(true);
    try {
      const result = await API.post('v1/discord_user_auths', {mode: 'login'});
      window.location = result?.url;
    } catch (e) {
      setError(e as ErrorWithDetails);
      setIsLoading(false);
    }
  };

  return (
    <div data-component="LoginPage">
      <div className="center-container">
        <div className="center-modal">
        <div className="form-intro">
            Login to <strong>Instant.bot</strong>
          </div>
          <div className="form-row login-discord">
            <Button
              color="blurple"
              size="medium"
              icon={DiscordLogo}
              label="Login with Discord"
              onClick={handleDiscordLogin}
              loading={isLoading}
              />
          </div>
          <div className="or-row">
            <div className="line"></div>
            <div className="or-text">OR</div>
            <div className="line"></div>
          </div>
          <div className="form-row">
            <Textbox
              error={error?.details?.email?.message}
              onChange={(value) => {
                error && error.clear && setError(error.clear('email'));
                setEmail(value);
              }}
              onSubmit={handleLogin}
              heading="email or username"
              defaultValue={query.get("email") || ""}
              autoFocus
              type="text"
              autoComplete={false}
              ref={emailRef}
              />
          </div>
          <div className="form-row">
          <Textbox
            error={error?.details?.password?.message}
            onChange={() => error && error.clear && setError(error.clear('password'))}
            onSubmit={handleLogin}
            type="password"
            heading="password"
            defaultValue={""}
            ref={passwordRef}
            />
          </div>
          {error &&
            !error?.details?.email &&
            !error?.details?.password && (
            <div className="form-row margin-top error">
              {error.message}
            </div>
          )}
          <div className="form-row margin-top align-right">
            <div className="link">
              <InternalLink to={`/signup${email ? `?email=${encodeURIComponent(email)}` : ``}`}>
                Need to register?
              </InternalLink>
            </div>
            <div className="spacer" />
            <Button
              loading={isLoading}
              label="Login"
              onClick={handleLogin}
              />
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
